import { afterRenderEffect, Component, effect, input, Input, output, ViewContainerRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isEqual } from 'app/shared/utils';
import {
    Control,
    DomEvent,
    Handler,
    Icon,
    LatLng,
    LatLngBounds,
    Layer,
    LeafletEvent,
    LeafletMouseEvent,
    Map as LMap,
    Marker,
    MarkerOptions,
    Point as LPoint,
    Polyline,
} from 'leaflet';
import 'leaflet.visualclick';
import { Subject } from 'rxjs';
import { DrawDeletingPoint, DrawDeletingPolygon, DrawDeletingRectangle, DrawEditablePolygon, DrawPolygon, DrawRectangle } from '../draw';
import { Bounds } from '../models/bounds';
import { Point, Position } from '../models/geometry';
import { LFeaturesService } from '../services';
import { basemaps, LMapService } from '../services/l-map.service';


export class LayerGroup<K = string> extends Layer {

    private layers = new Map<K, Layer>();

    private map: LMap | null = null;

    onAdd(map: LMap) {
        this.map = map;
        this.eachLayer(map.addLayer);
        return this;
    }

    onRemove(map: LMap) {
        this.eachLayer(map.removeLayer);
        this.map = null;
        return this;
    }

    addLayer(id: K, layer: Layer) {
        this.layers.set(id, layer);
        if (this.map) {
            this.map.addLayer(layer);
        }
    }

    removeLayer(id: K) {
        const layer = this.layers.get(id);
        if (this.map && layer) {
            this.map.removeLayer(layer);
        }
        this.layers.delete(id);
    }

    clearLayers() {
        this.eachLayer((_, id) => this.removeLayer(id));
    }

    eachLayer(fn: (layer: Layer, id: K) => void) {
        this.layers.forEach(fn);
    }

    getLayer(id: K) {
        return this.layers.get(id);
    }
}


export interface MarkerFeature {
    geometry: Point;
    icon?: Icon;
}


LMap.mergeOptions({
    smoothWheelZoom: true,
    smoothSensitivity: 0.008,
});


interface WebkitWheelEvent extends WheelEvent {
    webkitDirectionInvertedFromDevice?: boolean;
}


const inverted = (e: WebkitWheelEvent) => e.webkitDirectionInvertedFromDevice === true;

const deltaY = (e: WebkitWheelEvent) =>
    (e.deltaMode === e.DOM_DELTA_PIXEL) ? e.deltaY :
    (e.deltaMode === e.DOM_DELTA_LINE) ? e.deltaY * 20 :
    (e.deltaMode === e.DOM_DELTA_PAGE) ? e.deltaY * 60 : 0;


class SmoothWheelZoom extends Handler {

    private _map: LMap;

    private isWheeling = false;

    private wheelMousePosition: LPoint;

    private centerPoint: LPoint;

    private wheelMouseLatLng: LatLng;

    private moved: boolean;

    private goalZoom: number;

    private prevCenter: LatLng;

    private prevZoom: number;

    private zoomAnimationId: number;

    private timeoutId: any;

    private zoom: number;

    private center: LPoint;

    private delta: number;

    private reported = false;

    addHooks() {
        DomEvent.on(this._map.getContainer(), 'wheel', this.onWheelScroll, this);
    }

    removeHooks() {
        DomEvent.off(this._map.getContainer(), 'wheel', this.onWheelScroll, this);
    }

    private onWheelScroll(e: Event) {
        if (!this.isWheeling) {
            this.onWheelStart(e as WheelEvent);
        }
        this.onWheeling(e as WheelEvent);
    }

    private onWheelStart(e: WheelEvent) {
        const map = this._map as any;
        this.isWheeling = true;
        this.delta = deltaY(e);
        this.wheelMousePosition = map.mouseEventToContainerPoint(e);
        this.wheelMouseLatLng = map.containerPointToLatLng(this.wheelMousePosition);
        this.centerPoint = map.getSize().divideBy(2);
        this.moved = false;

        map._stop();
        if (map._panAnim) {
            map._panAnim.stop();
        }

        this.goalZoom = map.getZoom();
        this.prevCenter = map.getCenter();
        this.prevZoom = map.getZoom();

        this.zoomAnimationId = requestAnimationFrame(() => this.updateWheelZoom());
    }

    private onWheeling(e: WebkitWheelEvent) {
        const map = this._map as any;

        const direction = inverted(e) ? 1 : -1;
        let delta = deltaY(e);

        if (delta === 0) {
            delta = this.delta;
        }

        if (delta === 0 && !this.reported) {
            const serialized = {
                ...e,
                deltaMode: e.deltaMode,
                deltaX: e.deltaX,
                deltaY: e.deltaY,
                wheelDelta: (e as any).wheelDelta,
                wheelDeltaX: (e as any).wheelDeltaX,
                wheelDeltaY: (e as any).wheelDeltaY,
            };
            console.error(serialized);
            this.reported = true;
        }

        this.goalZoom = this.goalZoom + delta * direction * map.options.smoothSensitivity;
        if (this.goalZoom < map.getMinZoom() || this.goalZoom > map.getMaxZoom()) {
            this.goalZoom = map._limitZoom(this.goalZoom);
        }
        this.wheelMousePosition = map.mouseEventToContainerPoint(e);
        this.wheelMouseLatLng = map.containerPointToLatLng(this.wheelMousePosition);

        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => this.onWheelEnd(), 200);

        DomEvent.preventDefault(e);
        DomEvent.stopPropagation(e);
    }

    private onWheelEnd() {
        const map = this._map as any;
        this.isWheeling = false;
        cancelAnimationFrame(this.zoomAnimationId);
        map._moveEnd(true);
    }

    private updateWheelZoom() {
        const map = this._map as any;

        if ((!map.getCenter().equals(this.prevCenter)) || map.getZoom() != this.prevZoom) {
            return;
        }

        this.zoom = map.getZoom() + (this.goalZoom - map.getZoom()) * 0.3;
        this.zoom = Math.floor(this.zoom * 20) / 20;

        const delta = this.wheelMousePosition.subtract(this.centerPoint);
        if (delta.x === 0 && delta.y === 0) {
            return;
        }

        this.center = map.unproject(map.project(this.wheelMouseLatLng, this.zoom).subtract(delta), this.zoom);

        if (!this.moved) {
            map._moveStart(true, false);
            this.moved = true;
        }

        map._move(this.center, this.zoom);
        this.prevCenter = map.getCenter();
        this.prevZoom = map.getZoom();

        this.zoomAnimationId = requestAnimationFrame(() => this.updateWheelZoom());
    }
}


LMap.addInitHook('addHandler', 'smoothWheelZoom', SmoothWheelZoom);


@Component({
    selector: 'l-map',
    template: '',
    providers: [LMapService, LFeaturesService],
})
export class LMapComponent {

    private selection: Handler | null = null;

    private deleting: any;

    boundsChange = output<Bounds>();

    identify = output<Position>();

    selectProperty = output<Position>();

    center = input<Position>();

    zoom = input<number>();

    @Input()
    set basemap(basemapKey: string) {
        const basemap = basemaps.find(bm => bm.key === basemapKey);
        if (basemap) {
            this.lMapService.setBasemap(basemap);
        }
    }

    minZoom = input<number>();

    private _centerMarker?: L.Marker;

    @Input()
    set centerMarker(feature: MarkerFeature | undefined) {
        if (this._centerMarker) {
            if (!feature) {
                this._centerMarker.remove();
                this._centerMarker = undefined;
            } else {
                this._centerMarker.setLatLng([feature.geometry.coordinates[1], feature.geometry.coordinates[0]]);
            }
        } else if (feature) {
            const markerOptions: MarkerOptions = {
                zIndexOffset: 1000,
            };
            if (feature.icon) {
                markerOptions.icon = feature.icon;
            }
            const marker = new Marker([feature.geometry.coordinates[1], feature.geometry.coordinates[0]], markerOptions);
            marker.on('click', (e: LeafletMouseEvent) => {
                this.centerMarkerClick.emit([e.latlng.lng, e.latlng.lat]);
            });
            marker.addTo(this.lMapService.getMap());
        }
    }

    centerMarkerClick = output<Position>();

    drawCreated = new Subject<Polyline>();

    drawCanceled = new Subject<void>();

    drawDeleted = new Subject<string[]>();

    constructor(public lMapService: LMapService, vRef: ViewContainerRef) {
        this.lMapService.createMap(vRef.element);
        const map = this.lMapService.getMap();

        this.lMapService.on('click', (event: Position) => {
            this.identify.emit(event);
        });

        this.lMapService.on('contextmenu', (point: Position) => {
            this.selectProperty.emit(point);
        });

        const boundsChange = toSignal(this.lMapService.getBounds(), { equal: isEqual });
        effect(() => {
            const bounds = boundsChange();
            if (typeof bounds !== 'undefined') {
                this.boundsChange.emit(bounds);
            }
        });

        map.on('draw:created', (event: { layer: Polyline }) => {
            this.drawCreated.next(event.layer);
        });

        map.on('draw:cancel', () => {
            this.drawCanceled.next();
        });

        map.on('draw:deleted', (event: LeafletEvent) => {
            const layers = (event as any).layers as string[] | undefined;
            if (!layers) {
                return;
            }

            this.drawDeleted.next(layers);
        });

        effect(() => {
            const minZoom = this.minZoom();
            if (typeof minZoom !== 'undefined') {
                this.lMapService.getMap().setMinZoom(minZoom);
            }
        });

        // we need to wait until first render of the element to let map compute the correct height
        afterRenderEffect(() => {
            const zoom = this.zoom();
            const center = this.center();
            if (typeof zoom !== 'undefined' && typeof center !== 'undefined') {
                this.setZoom(zoom);
                this.setCenter(center);
            }
        });
    }

    setZoom(zoom: number) {
        this.lMapService.getMap().setZoom(zoom);
    }

    panTo(position: Position | null, zoom: number | null = null) {
        const destination = position ? new LatLng(position[1], position[0]) : this.lMapService.getMap().getCenter();
        if (!destination) {
            return;
        }
        const targetZoom = zoom || this.lMapService.getMap().getZoom();
        if (targetZoom) {
            // targetZoom might not be set (initial load) - zoom just in case we have it
            this.lMapService.getMap().setView(destination, targetZoom, { animate: false });
        } else {
            this.lMapService.getMap().panTo(destination, { animate: false });
        }
    }

    fitBounds(points: Position[]) {
        this.lMapService.getMap().fitBounds(new LatLngBounds(points.map(p => [p[1], p[0]])).pad(0.5));
    }

    setCenter(position: Position) {
        this.lMapService.setCenter(position);
    }

    startDeletingPoint() {
        this.stopDeleting();
        this.stopDrawing();

        this.deleting = new DrawDeletingPoint(this.lMapService.getMap(), {
            featureGroup: this.lMapService.getGroup('properties'),
        });
        this.deleting.enable();
    }

    confirmDelete() {
        if (this.deleting) {
            this.deleting.save();
            this.stopDeleting();
        }
    }

    cancelDelete() {
        if (this.deleting) {
            this.deleting.revertLayers();
            this.stopDeleting();
        }
    }

    stopDrawing() {
        if (this.selection) {
            this.selection.disable();
            this.selection = null;
        }
    }

    stopDeleting() {
        if (this.deleting) {
            this.deleting.disable();
            this.deleting = null;
        }
    }

    stopAllTools() {
        if (this.deleting) {
            this.deleting.clearLayers();
        }
        this.stopDeleting();
        this.stopDrawing();
    }

    startDeletingPolygon() {
        this.stopDeleting();
        this.stopDrawing();

        this.deleting = new DrawDeletingPolygon(
            this.lMapService.getMap(),
            { featureGroup: this.lMapService.getGroup('properties') }
        );
        this.deleting.enable();
    }

    startDeletingRectangle() {
        this.stopDeleting();
        this.stopDrawing();

        this.deleting = new DrawDeletingRectangle(
            this.lMapService.getMap(),
            { featureGroup: this.lMapService.getGroup('properties') }
        );
        this.deleting.enable();
    }

    startDrawingPolygon() {
        this.stopDrawing();
        this.selection = new DrawPolygon(this.lMapService.getMap());
        this.selection.enable();
    }

    startDrawingEditablePolygon() {
        this.stopDrawing();
        this.selection = new DrawEditablePolygon(this.lMapService.getMap());
        this.selection.enable();
    }

    startMeasurePolygon() {
        this.stopDrawing();
        this.selection = new DrawPolygon(this.lMapService.getMap(), { areaLimit: 0 });
        this.selection.enable();
    }

    startDrawingRectangle() {
        this.stopDrawing();
        this.selection = new DrawRectangle(this.lMapService.getMap());
        this.selection.enable();
    }

    addControl(control: Control) {
        this.lMapService.addControl(control);
    }

    ngOnDestroy() {
        this.lMapService.clearHandlers();
    }
}
